import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import { serverDomain } from "../../config";
import ExpandableText from "../../components/ExpandableText";
import "../../styles/LogsPage.css";

const fmt = (d) => {
  try {
    return new Date(d).toLocaleString("ru-RU");
  } catch {
    return "—";
  }
};

const codeClass = (code) => {
  const n = Number(code);
  if (!Number.isFinite(n)) return "fl-code fl-code--na";
  if (n >= 200 && n < 300) return "fl-code fl-code--ok";
  if (n >= 300 && n < 400) return "fl-code fl-code--warn";
  return "fl-code fl-code--bad";
};

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI filters
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchLogs = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${serverDomain}/api/logs/postback-logs`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const contentType = res.headers.get("content-type") || "";
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Ошибка ответа сервера: ${res.status}${text ? ` — ${text}` : ""}`);
      }

      if (!contentType.includes("application/json")) {
        const text = await res.text().catch(() => "");
        throw new Error(`Неверный тип ответа (ожидался JSON). ${text ? `Ответ: ${text}` : ""}`);
      }

      const data = await res.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setLogs([]);
      setError(e.message || "Ошибка при загрузке логов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const types = useMemo(() => {
    const set = new Set((logs || []).map((l) => l?.type).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [logs]);

  const filtered = useMemo(() => {
    const query = (q || "").trim().toLowerCase();

    return (logs || []).filter((l) => {
      if (typeFilter !== "all" && String(l.type) !== String(typeFilter)) return false;
      if (!query) return true;

      const hay = [
        l.type,
        l.link_name,
        l.postback_url,
        l.response_body,
        l.response_code,
      ]
        .map((x) => (x == null ? "" : String(x).toLowerCase()))
        .join(" ");

      return hay.includes(query);
    });
  }, [logs, q, typeFilter]);

  return (
    <div>
      <TopBar pageTitle={"Логи Postback"} />

      <div className="main-container">
        <SideBar />

        <main className="home-main flex-grow-1">
          <div className="fl-page fl-logs">
            <section className="fl-cardbox">
              <div className="fl-cardbox__head">
                <h2 className="fl-h2">Логи Postback</h2>

                <div className="fl-logs__headActions">
                  <input
                    className="fl-input fl-input--wide fl-logs__search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Поиск: поток / URL / ответ / код…"
                  />

                  <select
                    className="fl-select fl-logs__type"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    aria-label="Тип"
                  >
                    {types.map((t) => (
                      <option key={t} value={t}>
                        {t === "all" ? "Все типы" : t}
                      </option>
                    ))}
                  </select>
                  <button
                    className="fl-btn fl-btn--ghost fl-logs__refresh"
                    type="button"
                    onClick={fetchLogs}
                    disabled={loading}
                  >
                    {loading ? "Загрузка…" : "Обновить"}
                  </button>
                </div>
              </div>

              {error ? <div className="fl-alert fl-alert--error">{error}</div> : null}

              {loading ? (
                <div className="loading-container">
                  <div className="loader" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="fl-empty">Логов пока нет</div>
              ) : (
                <div className="fl-table-wrap">
                  <table className="fl-table fl-logs__table">
                    <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Тип</th>
                        <th className="fl-hide-sm">Поток</th>
                        <th>Ссылка</th>
                        <th>Код</th>
                        <th className="fl-hide-sm">Ответ</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filtered.map((log, idx) => (
                        <tr key={`${log.id || ""}_${idx}`}>
                          <td>{fmt(log.created_at)}</td>
                          <td>{log.type || "—"}</td>
                          <td className="fl-hide-sm">{log.link_name || "—"}</td>

                          <td className="fl-logs__cell">
                            {/* Можно кликнуть и развернуть (ExpandableText) */}
                            <div className="fl-logs__expand" title={log.postback_url || ""}>
                              <ExpandableText text={log.postback_url || ""} limit={72} />
                            </div>
                          </td>

                          <td>
                            <span className={codeClass(log.response_code)}>{log.response_code ?? "—"}</span>
                          </td>

                          <td className="fl-hide-sm fl-logs__cell">
                            <div className="fl-logs__expand" title={log.response_body || ""}>
                              <ExpandableText text={log.response_body || ""} limit={120} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LogsPage;